// @ts-strict-ignore
import * as React from 'react';
import Collapse from 'antd/lib/collapse';
import { ItemType } from 'rc-collapse/es/interface';
import { antGrey, runwayColors, titleStyle } from '@/components/styles/globalStyles';
import Space from 'antd/lib/space';
import Divider from 'antd/lib/divider';
import Tag from 'antd/lib/tag';
import Text from 'antd/lib/typography/Text';
import { iconAndAltForIntegrationType } from '@/helpers/integrationHelpers';
import {
    AppStoreAvailableBuild,
    AppStoreReviewSubmissionState,
    IntegrationType,
    ReviewSubmission,
    AppStoreReviewSubmissionItemType,
    AppStoreReviewSubmissionItemState,
} from '@/types';
import { STRING_FOR_APP_STORE_REVIEW_SUBMISSION_STATE } from '@/helpers';
import { reviewItemNameForType, statusColorForReviewSubmissionState } from '@/helpers/displayHelpers';

export interface AppStoreSubmissionCollapseProps {
    reviewSubmission?: ReviewSubmission;
    selectedBuilds?: AppStoreAvailableBuild[];
    isAppVersionInsideReviewSubmission?: boolean;
    isReviewSubmissionBlocked?: boolean;
    integrationId: IntegrationType;
    shouldHideReviewSubmissionInterfaces?: boolean;
    shouldHideSubmissionStatusTag?: boolean;
}

export const AppStoreSubmissionCollapse: React.FC<React.PropsWithChildren<AppStoreSubmissionCollapseProps>> = ({
    children,
    integrationId,
    reviewSubmission,
    selectedBuilds,
    isAppVersionInsideReviewSubmission,
    isReviewSubmissionBlocked,
    shouldHideReviewSubmissionInterfaces,
    shouldHideSubmissionStatusTag,
}) => {
    const [icon, alt] = iconAndAltForIntegrationType(integrationId);

    const { state, types } = stateAndTypeForHeader(
        isReviewSubmissionBlocked,
        isAppVersionInsideReviewSubmission,
        selectedBuilds,
        reviewSubmission,
    );

    const collapseHeaderStateText = STRING_FOR_APP_STORE_REVIEW_SUBMISSION_STATE[state] ?? 'Not ready to submit';
    const collapseHeaderStateColor = statusColorForReviewSubmissionState(state);
    const collapseHeaderSummaryText = reviewSubmissionItemsSummaryText(types);

    const content = (
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 20,
                width: '100%',
            }}
        >
            {children}
        </div>
    );

    if (shouldHideReviewSubmissionInterfaces) {
        return content;
    }

    const collapsibleItem: ItemType = {
        label: (
            <Space split={<Divider type="vertical" style={{ backgroundColor: antGrey.grey3 }} />}>
                <Space>
                    <img src={icon} alt={alt} width={24} height={24} />
                    {collapseHeaderStateText && !shouldHideSubmissionStatusTag && (
                        <Tag
                            style={{
                                ...titleStyle,
                                fontSize: 11,
                                lineHeight: '22px',
                                fontWeight: 700,
                                color: runwayColors.white,
                                margin: 0,
                            }}
                            color={collapseHeaderStateColor}
                        >
                            {collapseHeaderStateText}
                        </Tag>
                    )}
                </Space>
                {collapseHeaderSummaryText && (
                    <Text style={{ fontSize: 14, color: antGrey.grey7 }}>{collapseHeaderSummaryText}</Text>
                )}
            </Space>
        ),
        children: content,
        key: '1',
        style: {
            padding: '6px 0',
        },
    };
    return (
        <Collapse
            defaultActiveKey={['1']}
            className="submission-collapse"
            style={{
                backgroundColor: runwayColors.white,
                borderRadius: 12,
                border: `1px solid ${antGrey.grey5}`,
            }}
            items={[collapsibleItem]}
        />
    );
};

const stateAndTypeForHeader = (
    isReviewSubmissionBlocked: boolean,
    isAppVersionInsideReviewSubmission: boolean,
    selectedBuilds: AppStoreAvailableBuild[],
    reviewSubmission?: ReviewSubmission,
): { state: AppStoreReviewSubmissionState; types: AppStoreReviewSubmissionItemType[] } => {
    const hasReviewSubmission = !!reviewSubmission;
    let state = AppStoreReviewSubmissionState.ReadyForReview;
    let types: AppStoreReviewSubmissionItemType[] = [];
    let relevantReviewSubmissionItems = reviewSubmission?.reviewSubmissionItems || [];

    relevantReviewSubmissionItems = relevantReviewSubmissionItems.filter(
        (item) => item.state !== AppStoreReviewSubmissionItemState.Removed,
    );

    if (!isAppVersionInsideReviewSubmission && (!selectedBuilds || selectedBuilds.length === 0)) {
        state = null; // so we display the default "not ready to submit" state
    }

    if (isReviewSubmissionBlocked) {
        state = AppStoreReviewSubmissionState.Blocked;
        types = [AppStoreReviewSubmissionItemType.AppBuildVersion];
    } else if (!isAppVersionInsideReviewSubmission && selectedBuilds?.length) {
        types = types.concat(selectedBuilds.map(() => AppStoreReviewSubmissionItemType.AppBuildVersion));
        relevantReviewSubmissionItems = relevantReviewSubmissionItems.filter(
            (item) => item.type !== AppStoreReviewSubmissionItemType.AppBuildVersion,
        );
    }

    if (hasReviewSubmission) {
        state = reviewSubmission.state;
        types = types.concat(relevantReviewSubmissionItems.map((item) => item.type));
    }

    return { state, types };
};

export const reviewSubmissionItemsSummaryText = (types: AppStoreReviewSubmissionItemType[]): string => {
    const totalItems = types.length;
    if (totalItems === 0) {
        return null;
    }

    const groupedItemsByType = types.reduce(
        (acc, type) => {
            if (!acc[type]) {
                acc[type] = 0;
            }
            acc[type]++;
            return acc;
        },
        {} as Record<AppStoreReviewSubmissionItemType, number>,
    );

    const totalItemsString = Object.entries(groupedItemsByType)
        .map(([type, count]) => {
            const isPlural = count > 1;
            return `${count} ${reviewItemNameForType(type as AppStoreReviewSubmissionItemType).toLowerCase()}${
                isPlural ? 's' : ''
            }`;
        })
        .join(', ');
    return `${totalItems} ${totalItems > 1 ? 'items' : 'item'}: ${totalItemsString}`;
};
